import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { SellerOrdersReport_sellerOrders_edges_node } from "@saleor/sellerOrders/types/SellerOrdersReport";
import { ListProps } from "@saleor/types";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      colMedium: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        overflowing,
        width: "16em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      table: {
        tableLayout: "fixed"
      },
      tableRow: {
        cursor: "pointer"
      }
    };
  },
  { name: "SellerOrdersList" }
);

export interface SellerOrdersListProps extends ListProps {
  sellerOrders: SellerOrdersReport_sellerOrders_edges_node[];
}

export const SellerOrdersList: React.FC<SellerOrdersListProps> = ({
  sellerOrders,
  onNextPage,
  onPreviousPage,
  onUpdateListSettings,
  pageInfo,
  settings,
  disabled
}) => {
  const colNum = 5;
  const classes = useStyles({});

  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="ID" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Mall Name" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Brand Name" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Vertical" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Order Status" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Store Order Status" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Order Created Date" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Delivery Schedule" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Order Completion Date" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Store Order Completion Date" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Order Code" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Store Order Code" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Delivery Type" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Payment Method" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Voucher Code" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Total Gross Amount" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Total Discount" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Total Sales Amount" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Delivery Fee" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Total Voucher" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Price Paid" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Order Cancelation Reason" />
            </TableCellHeader>
            <TableCellHeader className={classes.colWide}>
              <FormattedMessage defaultMessage="Store Order Cancelation Reason" />
            </TableCellHeader>
          </TableRow>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {sellerOrders &&
            renderCollection(
              sellerOrders,
              (sellerOrders, index) => (
                <TableRow
                  className={!!sellerOrders ? classes.tableRow : undefined}
                  hover={!!sellerOrders}
                  key={sellerOrders ? sellerOrders.id : "skeleton"}
                >
                  <TableCell id={`sellerOrdersIdRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => atob(sellerOrders.id),
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`mallNameRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.mall_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`brandNameRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.brand_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`verticalRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.vertical,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`orderStatusRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.order_status?.toUpperCase(),
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`storeOrderStatusRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.store_order_status?.toUpperCase(),
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`orderDateRow${index}`}>
                    <Moment format="YYYY-MM-DD HH:mm:ss" tz="Asia/Manila">
                      {sellerOrders?.order_date ? (
                        sellerOrders.order_date
                      ) : (
                        <Skeleton />
                      )}
                    </Moment>
                  </TableCell>
                  <TableCell id={`shippingScheduleRow${index}`}>
                    <Moment format="YYYY-MM-DD HH:mm:ss" tz="Asia/Manila">
                      {sellerOrders?.shipping_schedule ? (
                        sellerOrders.shipping_schedule
                      ) : (
                        <Skeleton />
                      )}
                    </Moment>
                  </TableCell>
                  <TableCell id={`completedAtRow${index}`}>
                    {sellerOrders?.completed_at
                      ? moment
                          .tz(
                            new Date(sellerOrders?.completed_at),
                            "Asia/Manila"
                          )
                          .format("YYYY-MM-DD HH:mm:ss")
                      : null}
                  </TableCell>
                  <TableCell id={`storeCompletedAtRow${index}`}>
                    {sellerOrders?.store_completed_at
                      ? moment
                          .tz(
                            new Date(sellerOrders?.store_completed_at),
                            "Asia/Manila"
                          )
                          .format("YYYY-MM-DD HH:mm:ss")
                      : null}
                  </TableCell>
                  <TableCell id={`orderCodeRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.order_code,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`storeOrderCodeRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.store_order_code,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`shippingMethodNameRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.shipping_method_name,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`paymentMethodTypeRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.payment_method_type?.toUpperCase(),
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`voucherCodeRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.voucher_code,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`retailPriceRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.retail_price,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`salesDiscountTotalRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.sales_discount_total,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`sellingPriceRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.selling_price,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`deliveryFeeRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.delivery_fee,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`voucherDiscountTotalRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.voucher_discount_total,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`pricePaidRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.price_paid,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`orderCancelReasonRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.order_cancel_reason,
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell id={`storeOrderCancelReasonRow${index}`}>
                    {maybe<React.ReactNode>(
                      () => sellerOrders.store_order_cancel_reason,
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell colSpan={colNum} id="noRecordsFound">
                    <FormattedMessage defaultMessage="No records found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default SellerOrdersList;
